import { useEffect, useState } from 'react';
import { Share2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUpdateProfile } from '@/features/profile/hooks/useUpdateProfile';
import { useToast } from '@/shared/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { type BeltColor } from '@/constants/belt';
import { OnboardingShell } from './OnboardingShell';
import { markOptionalFieldsCompleted } from './markOptionalFieldsCompleted';
import { OnboardingStep1Profile } from './steps/OnboardingStep1Profile';
import { InstructorStep3Agenda } from './steps/InstructorStep3Agenda';
import { ONBOARDING_STEPS, ONBOARDING_TOTAL_STEPS } from './onboardingSteps';

interface InstructorOnboardingProps {
  username: string;
  onComplete: (destination?: string) => void;
}

const COMPLETE_PROFILE = ONBOARDING_STEPS.COMPLETE_PROFILE;
const AGENDA = ONBOARDING_STEPS.FINAL;

export const InstructorOnboarding = ({ username, onComplete }: InstructorOnboardingProps) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const updateProfile = useUpdateProfile();

  const [step, setStep] = useState(COMPLETE_PROFILE);
  const [name, setName] = useState('');
  const [belt, setBelt] = useState<BeltColor>('white');
  const [degrees, setDegrees] = useState(0);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [academyId, setAcademyId] = useState('');
  const [academyName, setAcademyName] = useState('');

  useEffect(() => {
    if (step === AGENDA) void markOptionalFieldsCompleted(username);
  }, [step, username]);

  const saveProfile = async () => {
    try {
      await updateProfile.mutateAsync({
        full_name: name,
        belt_rank: belt,
        stripe_rank: degrees,
        ...(photoUrl ? { profile_picture_url: photoUrl } : {}),
        ...(academyName ? { jiu_jitsu_school: academyName } : {}),
        ...(academyId ? { main_academy_id: academyId } : {}),
      });
      setStep(AGENDA);
    } catch {
      toast({ title: t('onboarding.instructor.save_error_title'), description: t('onboarding.instructor.save_error_description'), variant: 'destructive' });
    }
  };

  const markComplete = () => markOptionalFieldsCompleted(username);

  const handleComplete = async (destination = `/instructor-slots/${username}`) => {
    await markComplete();
    onComplete(destination);
  };

  const appBase = import.meta.env.VITE_APP_URL || 'https://app.sossiety.com';
  const step3FullUrl = `${appBase}/i/${username}`;

  const handleStep3Share = async () => {
    await markComplete();
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('onboarding.instructor.share_title'),
          text: t('onboarding.instructor.share_text'),
          url: step3FullUrl,
        });
      } catch { /* user dismissed */ }
    } else {
      await navigator.clipboard.writeText(step3FullUrl);
      toast({ description: t('onboarding.instructor.link_copied') });
    }
  };

  const handleStep3ViewProfile = () => handleComplete(`/i/${username}`);

  const stickyFooter = step === COMPLETE_PROFILE ? (
    <Button
      variant="primary"
      size="lg"
      disabled={name.trim().length === 0}
      onClick={saveProfile}
      className="w-full"
    >
      {t('onboarding.continue')}
    </Button>
  ) : (
    <div className="space-y-3">
      <Button
        type="button"
        variant="primary"
        size="lg"
        onClick={handleStep3Share}
        className="h-[45px] w-full gap-2"
      >
        <Share2 className="h-4 w-4" />
        {t('onboarding.instructor.share_now')}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={handleStep3ViewProfile}
        className="h-[45px] w-full gap-2 border-[rgba(255,255,255,0.15)] text-white/70 hover:border-white/30 hover:text-white"
      >
        <ExternalLink className="h-4 w-4" />
        {t('onboarding.instructor.view_public_profile')}
      </Button>
    </div>
  );

  return (
    <OnboardingShell
      step={step}
      totalSteps={ONBOARDING_TOTAL_STEPS}
      title={step === COMPLETE_PROFILE ? t('onboarding.complete_profile_title') : undefined}
      subtitle={step === COMPLETE_PROFILE ? t('onboarding.complete_profile_subtitle') : undefined}
      stickyFooter={stickyFooter}
    >
      {step === COMPLETE_PROFILE && (
        <OnboardingStep1Profile
          username={username}
          name={name}
          belt={belt}
          degrees={degrees}
          photoUrl={photoUrl}
          academyId={academyId}
          academyName={academyName}
          onNameChange={setName}
          onBeltChange={setBelt}
          onDegreesChange={setDegrees}
          onPhotoChange={setPhotoUrl}
          onAcademyChange={(id, n) => { setAcademyId(id); setAcademyName(n); }}
        />
      )}
      {step === AGENDA && (
        <InstructorStep3Agenda
          username={username}
          photoUrl={photoUrl}
          belt={belt}
          onMarkComplete={markComplete}
        />
      )}
    </OnboardingShell>
  );
};
